import type { RowData } from "@tanstack/react-table";
import type { DataGridRow } from "../columns/types";
import { closeListEditor } from "../editing/list/listEditActions";
import { collapseRange } from "../features/range/rangeActions";
import type { DataGridInstance } from "../types";
import { FOCUSABLE_SELECTOR } from "./focusLevel";
import type { GridController } from "./gridController";
import {
    getFocusedAddress,
    isSameCellAddress,
    selectFocusedRowId,
} from "./navigation";

/**
 * A list editor still open on the focused cell: Escape reached the cell
 * before the editor's popup took focus.
 */
const getStrandedListEditor = <TData extends RowData>(
    instance: DataGridInstance<TData>,
) => {
    const editor = instance.atoms.activeEditor.get();
    const focused = getFocusedAddress(instance.table.atoms.cellSelection.get());
    return instance.hasListEditing &&
        editor !== null &&
        focused !== null &&
        isSameCellAddress(editor, focused)
        ? editor
        : null;
};

/**
 * Escape order: close a list editor still open on the focused cell, from a
 * list editing cell to its row, collapse the range, clear row selection
 * (unless keyboard selection is off), leave the table. Returns false on
 * leaving, so the host's own Escape handling takes over.
 */
export const escapeGrid = <TData extends RowData>({
    instance,
    controller,
    target,
}: {
    instance: DataGridInstance<TData>;
    controller: GridController;
    target: EventTarget;
}) => {
    const { table } = instance;
    const strandedEditor = getStrandedListEditor(instance);
    if (strandedEditor !== null) {
        closeListEditor({ instance, controller }, strandedEditor);
        return true;
    }
    const focusedRowId = selectFocusedRowId(table.atoms.cellSelection.get());
    if (instance.atoms.listCellFocus.get() && focusedRowId !== null) {
        instance.atoms.listCellFocus.set(false);
        controller.focusTarget({ rowId: focusedRowId, columnId: null });
        return true;
    }
    if (collapseRange(table)) {
        return true;
    }
    const isKeyboardSelectionDisabled =
        instance.optionsRef.current.rowSelection?.isKeyboardSelectionDisabled;
    if (
        !isKeyboardSelectionDisabled &&
        Object.keys(table.atoms.rowSelection.get()).length > 0
    ) {
        table.setRowSelection({});
        return true;
    }
    controller.isFocusWithin.set(false);
    if (target instanceof HTMLElement) {
        target.blur();
    }
    return false;
};

const asGridCell = (target: EventTarget) =>
    target instanceof Element && target.getAttribute("role") === "gridcell"
        ? target
        : null;

const focusCellControls = (target: EventTarget) => {
    const control = asGridCell(target)
        ?.querySelector("[data-dt-controls]")
        ?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    control?.focus();
    return control !== null && control !== undefined;
};

const activateCellLink = (target: EventTarget) => {
    const link =
        asGridCell(target)?.querySelector<HTMLElement>("[data-dt-activate]");
    link?.click();
    return link !== null && link !== undefined;
};

/**
 * Enter on the active cell: an actions cell (`[data-dt-controls]`) moves
 * focus into its controls; a cell whose one control is a link
 * (`[data-dt-activate]`, set by `LinkCell`) follows it in place.
 */
export const enterCell = (target: EventTarget) =>
    focusCellControls(target) || activateCellLink(target);

/** Opens the row through `onRowOpen`; false when the grid has none. */
export const openRow = <TData extends RowData>({
    instance,
    row,
    columnId,
}: {
    instance: DataGridInstance<TData>;
    row: DataGridRow<TData>;
    columnId: string | undefined;
}) => {
    const { onRowOpen } = instance.optionsRef.current;
    onRowOpen?.(row, columnId);
    return onRowOpen !== undefined;
};

export const focusSearch = (controller: GridController) => {
    controller.focusSearch?.();
    return controller.focusSearch !== null;
};
